import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "@/i18n/format";
import { notificationRoute } from "@/lib/notificationTarget";
import type {
  ModeratorsNotificationsList200Item,
  ModeratorsNotificationsList200ItemResultsItem,
} from "@/services/moderators/generated/model";
import {
  getModeratorsNotificationsListQueryKey,
  useModeratorsNotificationsList,
  useModeratorsNotificationsMarkAllReadCreate,
  useModeratorsNotificationsMarkReadCreate,
} from "@/services/moderators/generated/moderators-notifications/moderators-notifications";

/** Moderator-facing notifications (e.g. new company registrations) with an unread count. */
const ModeratorNotificationBell = () => {
  const { t } = useTranslation(["layout", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useModeratorsNotificationsList({ page: 1, page_size: 10 });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: getModeratorsNotificationsListQueryKey() });
  const markRead = useModeratorsNotificationsMarkReadCreate({ mutation: { onSuccess: invalidate } });
  const markAllRead = useModeratorsNotificationsMarkAllReadCreate({ mutation: { onSuccess: invalidate } });

  const page = (Array.isArray(data) ? data[0] : data) as ModeratorsNotificationsList200Item | undefined;
  const notifications: ModeratorsNotificationsList200ItemResultsItem[] = page?.results ?? [];
  const unreadCount = page?.unreadCount ?? notifications.filter((n) => !n.isRead).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpen = (n: ModeratorsNotificationsList200ItemResultsItem) => {
    if (!n.isRead && n.id != null) markRead.mutate({ id: n.id });
    const route = notificationRoute(n, "moderator");
    if (route) {
      setOpen(false);
      navigate(route.path, { state: route.state });
    }
  };

  return (
    <div className="moderator-bell" ref={wrapperRef}>
      <button
        type="button"
        className="moderator-bell__button"
        aria-label={
          unreadCount > 0 ? t("notifications.titleWithUnread", { count: unreadCount }) : t("notifications.title")
        }
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <i className="fa-regular fa-bell" aria-hidden />
        {unreadCount > 0 && <span className="moderator-bell__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>
      {open && (
        <div className="moderator-bell__panel">
          <div className="moderator-bell__panel-header">
            <span>{t("notifications.title")}</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="moderator-bell__mark-all"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>
          {isLoading ? (
            <div className="moderator-bell__empty">{t("common:states.loading")}</div>
          ) : notifications.length === 0 ? (
            <div className="moderator-bell__empty">{t("notifications.empty")}</div>
          ) : (
            <ul className="moderator-bell__list">
              {notifications.map((n) => (
                <li key={n.id ?? n.createdAt}>
                  <button
                    type="button"
                    className={`moderator-bell__item ${n.isRead ? "moderator-bell__item--read" : ""}`}
                    onClick={() => handleOpen(n)}
                  >
                    <span className="moderator-bell__item-title">{n.title ?? t("notifications.fallbackTitle")}</span>
                    <span className="moderator-bell__item-message">{n.message ?? ""}</span>
                    <span className="moderator-bell__item-time">{n.createdAt ? formatDateTime(n.createdAt) : ""}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ModeratorNotificationBell;
